import { AppendReason, RemoveReason, StringPropertyTextBlock, ViewUpdate, VList, VObjectInList } from "@1oo4/miv";
import { MArticle, MChapter, MContent, MSection } from "../../shared/model/mdoc";
import { DocPage, VRootDoc } from ".";
import { runAsync } from "../../shared/pages";
import { VContent } from "./contents";
import { navigateAsync, preventNavigation } from "../..";

class HighlightAnimation {
    private readonly target: HTMLElement;

    constructor(target: HTMLElement) {
        this.target = target;
        target.classList.add('highlightable');
        // Optional: clean up the class once the animation ends, so the element returns to a "default" state with no lingering class.
        target.addEventListener(
            "animationend",
            () => target.classList.remove("highlight-blink")//,
            //{ once: true }
        );
    }

    trigger(): void {
        // Remove the class first in case the animation is already running, so re-triggering restarts it cleanly.
        this.target.classList.remove("highlight-blink");
        requestAnimationFrame(() => this.target.classList.add("highlight-blink"));
        // code qui marche pas :
        // Force a reflow so the browser "notices" the class was removed before we add it again — otherwise the animation won't restart.
        //void this.target.offsetWidth;
        //this.target.classList.add("highlight-blink");
    }
}

export class VChapterDoc extends VObjectInList<MChapter, VRootDoc> {

    private readonly view: HTMLDivElement;
    private readonly arrow: HTMLSpanElement;
    readonly panelArticles: HTMLDivElement;
    readonly articles: VList<VArticleDoc, MArticle>;
    private readonly highlight: HighlightAnimation;

    constructor(m: MChapter, root: VRootDoc) {
        super(m, root);

        const template = document.getElementById("tpl_doc_chapter") as HTMLTemplateElement
        this.view = (template.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;

        const name = new StringPropertyTextBlock(this.view.querySelector(".doc_chapter_name") as HTMLInputElement);
        name.Setup(m.Name, this);

        this.panelArticles = this.view.querySelector(".doc_articles_panel") as HTMLDivElement;
        this.articles = new VList(m.Articles, this, x => new VArticleDoc(x, this));

        this.arrow = this.view.querySelector(".doc_treeview_arrow_right") as HTMLSpanElement;
        const collexArea = this.view.querySelector(".doc_chapter_header") as HTMLDivElement;
        collexArea.onclick = () => {
            if (this.panelArticles.style.display == "none") this.expand();
            else this.collapse();
        }

        this.highlight = new HighlightAnimation(name.HTMLSpan);

        this.collapse();
    }

    collapse(): void {
        this.arrow.className = "doc_treeview_arrow_right";
        this.panelArticles.style.display = "none";
    }
    expand(): void {
        this.arrow.className = "doc_treeview_arrow_bottom";
        this.panelArticles.style.display = '';
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.page.panelChapters.appendChild(this.view);
        if (this.IndexInGUI == 0 && ar == AppendReason.APPEARED && this.Parent.page.isOpeningBasePath()) {
            this.expand();
        }
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.page.panelChapters.removeChild(this.view);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.page.panelChapters.insertBefore(this.view, this.Parent.page.panelChapters.children[index]);
    }

    bringIntoView(highlight: boolean) {
        this.Parent.page.expand();
        this.view.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
        });
        if (highlight) {
            this.highlight.trigger();
        }
    }
}

export class VArticleDoc extends VObjectInList<MArticle, VChapterDoc> {

    private readonly view: HTMLDivElement;
    private readonly name: HTMLAnchorElement;
    private sections_sought: boolean = false;
    readonly panelSections: HTMLDivElement;
    readonly panelSectionsList: HTMLDivElement;
    readonly sections: VList<VSectionDoc, MSection>;
    private readonly highlight: HighlightAnimation;
    private last_scroll: number | undefined = undefined;

    constructor(m: MArticle, parent: VChapterDoc) {
        super(m, parent);

        const template = document.getElementById("tpl_doc_article") as HTMLTemplateElement
        this.view = (template.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;

        this.name = this.view.querySelector(".doc_article_name") as HTMLAnchorElement;

        const nameBinding = new StringPropertyTextBlock(this.name);
        nameBinding.Setup(m.Name, this);

        const self = this;
        this.name.onclick = (evt) => {
            evt.stopPropagation();
            evt.preventDefault();
            runAsync(self.Parent.Parent.page.selectArticleAsync(self, true));
        }

        this.panelSections = document.createElement("div");
        this.panelSections.classList.add("scrollviewer");
        this.panelSections.classList.add("panel_article_content");

        this.sections = new VList(m.Sections, this, x => new VSectionDoc(x, this));

        this.panelSectionsList = this.view.querySelector(".doc_article_sectionlist_panel") as HTMLDivElement;

        this.highlight = new HighlightAnimation(this.name);
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.panelArticles.appendChild(this.view);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.panelArticles.removeChild(this.view);
        if (rr != RemoveReason.TEMPORARY && this.Parent.Parent.page.selectedArticle == this) {
            this.Parent.Parent.page.selectArticleAsync(undefined, false);
        }
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.panelArticles.insertBefore(this.view, this.Parent.panelArticles.children[index]);
    }

    protected ModelUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.Model.Name)) {
            this.name.href = DocPage.getArticleURL(this.Model);
        }
    }

    async setSelected(selected: boolean): Promise<void> {
        if (selected) {
            this.name.className = "doc_article_name_selected";
            this.panelSectionsList.style.display = 'block';
            this.Parent.Parent.page.panelSections.appendChild(this.panelSections);
            if (this.sections_sought) {
                if (this.last_scroll !== undefined) {
                    this.panelSections.scrollTop = this.last_scroll;
                }
            } else {
                this.sections_sought = true;
                await this.Model.LoadSectionsAndUpdateUIAsync();
            }
        } else {
            this.name.className = "doc_article_name";
            this.panelSectionsList.style.display = 'none';
            this.last_scroll = this.panelSections.scrollTop;
            this.Parent.Parent.page.panelSections.removeChild(this.panelSections);
        }
    }

    bringIntoView(highlight: boolean) {
        this.name.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
        });
        if (highlight) {
            this.highlight.trigger();
        }
    }
}

export class VSectionDoc extends VObjectInList<MSection, VArticleDoc> {

    private readonly view: HTMLDivElement;
    private readonly listView: HTMLSpanElement;
    private readonly tbName: StringPropertyTextBlock;
    readonly panelContent: HTMLDivElement;
    readonly contents: VList<VContent, MContent>;
    private readonly highlight: HighlightAnimation;

    constructor(m: MSection, parent: VArticleDoc) {
        super(m, parent);

        const tpl = document.getElementById("tpl_doc_section") as HTMLTemplateElement
        this.view = (tpl.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;

        const link = this.view.querySelector(".doc_section_linkbutton") as HTMLButtonElement;
        link.onclick = () => {
            const url = window.location.origin + DocPage.getSectionURL(m)
            navigator.clipboard.writeText(url);
            alert('"' + url + '" has been copied to the clipboard')
        }

        this.tbName = new StringPropertyTextBlock(this.view.querySelector(".doc_section_title") as HTMLSpanElement);
        this.tbName.Setup(m.Name, this);

        this.panelContent = this.view.querySelector(".stackpanel") as HTMLDivElement;

        this.contents = new VList(m.Contents, this, x => x.makeViewDoc(this));

        this.listView = document.createElement('span');
        this.listView.className = 'doc_article_sectionlistname';
        const listNameBinding = new StringPropertyTextBlock(this.listView);
        listNameBinding.Setup(m.Name, this);
        this.listView.onclick = (evt) => {
            evt.stopPropagation();
            this.Parent.Parent.Parent.page.selectSectionWithinSelectedArticle(this);
            //navigateAsync(DocPage.getSectionURL(m), false);
        }

        this.highlight = new HighlightAnimation(this.tbName.HTMLSpan);
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.panelSections.appendChild(this.view);
        this.Parent.panelSectionsList.appendChild(this.listView);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.panelSections.insertBefore(this.view, this.Parent.panelSections.children[index]);
        this.Parent.panelSectionsList.insertBefore(this.listView, this.Parent.panelSectionsList.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.panelSections.removeChild(this.view);
        this.Parent.panelSectionsList.removeChild(this.listView);
    }

    bringIntoView(highlight: boolean) {
        this.tbName.HTMLSpan.scrollIntoView({
            //behavior: 'smooth',
            behavior: 'instant',
            block: 'start',
            inline: 'nearest'
        });
        if (highlight) {
            this.highlight.trigger();
        }
    }
}